import React, { Fragment, useMemo, useRef } from "react";
import {
  useTable,
  useFilters,
  useExpanded,
  useRowSelect,
  useSortBy,
  useFlexLayout,
  usePagination
} from "react-table";
import makeStyles from "@mui/styles/makeStyles";
import { Box, Typography } from "@mui/material";
import MuiTable from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableContainer from "@mui/material/TableContainer";
import { useEffect } from "react";
import { IndeterminateCheckbox, SortCell } from "./Cells";
import clsx from "clsx";
import { useTranslation } from "react-i18next";
import CircularProgressOverlay from "../Table/CircularProgressOverlay";
import { PropTypes } from "prop-types";

import InfiniteLoader from "react-window-infinite-loader";
import { FixedSizeList } from "react-window";
import { useSticky } from "react-table-sticky";

const useStyles = makeStyles(theme => ({
  table: {
    display: "block"
  },
  tableHead: {
    display: "block"
  },
  tableBody: {
    position: "relative",
    display: "block"
  },
  noRecordsRow: {
    display: "flex"
  },
  noRecordsCell: {
    display: "flex",
    flex: 1,
    justifyContent: "center"
  },
  headerCell: {
    color: "white",
    fontSize: "0.875rem",
    position: "sticky",
    verticalAlign: "center",
    textAlign: "center",
    zIndex: 2,
    top: "0",
    padding: theme.spacing(0, 1.5),
    height: theme.spacing(6)
  },
  headerCaptionRow: {
    background: theme.gradient,
    backgroundSize: "cover",
    backgroundPosition: "center",
    backgroundAttachment: "fixed",
    zIndex: 3
  },
  header: {
    zIndex: 4,
    position: "sticky",
    top: 0
  },
  filter: {
    zIndex: 2,
    top: theme.spacing(6),
    position: "sticky",
    background: "white",
    padding: theme.spacing(0),
    border: "none",
    "&:after": {
      content: `''`,
      position: "absolute",
      left: 0,
      bottom: 0,
      width: "100%",
      borderBottom: `solid 1px ${theme.palette.divider}`
    }
  },
  tableContainer: {
    display: "block",
    overflow: "auto",
    maxWidth: "100%"
  },
  selectAll: {
    color: "white"
  },
  checkbox: {
    padding: 0
  },
  rowClass: {
    cursor: ({ isClickable }) => (isClickable ? "pointer" : "default")
  },
  rowExpanded: {
    backgroundColor: theme.palette.action.hover,
    "& > td": {
      borderBottom: "none"
    }
  },
  expansionPanel: {
    backgroundColor: theme.palette.action.hover
  },
  loadingOverlay: {
    position: "absolute",
    top: 0,
    left: 0,
    height: "100%",
    width: "100%",
    zIndex: 11
  }
}));

const getStyles = (props, align = "left") => [
  props,
  {
    style: {
      justifyContent: align === "right" ? "flex-end" : "flex-start",
      alignItems: "center",
      display: "flex"
    }
  }
];

const headerProps = (props, { column }) => getStyles(props, column.align);
const cellProps = (props, { cell }) => getStyles(props, cell.column.align);

const Table = props => {
  const {
    columns,
    data,
    onStateChange,
    updateSelection,
    showSelectAllCheckbox,
    detailPanel,
    showCheckbox,
    manualSortBy,
    manualFilters,
    loading,
    onRowClick,
    cellSize,
    rowHeight,
    tableHeight,
    pageSize,
    totalCount
  } = props;
  const { t } = useTranslation();
  const classes = useStyles({
    isClickable: !!detailPanel || !!onRowClick
  });

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    state,
    visibleColumns,
    toggleRowExpanded,
    selectedFlatRows,
    setSortBy,
    gotoPage
  } = useTable(
    {
      columns,
      data,
      manualFilters: manualFilters,
      manualSortBy: manualSortBy,
      autoResetSelectedRows: false,
      autoResetExpanded: false,
      manualPagination: true,
      initialState: { pageIndex: 1 },
      pageCount: -1,
      getRowId: row => row.id
    },
    useFilters,
    useSortBy,
    useExpanded,
    usePagination,
    useRowSelect,
    useFlexLayout,
    useSticky,
    hooks => {
      if (!!updateSelection) {
        hooks.visibleColumns.push(columns => [
          {
            id: "selection",
            Header: ({ getToggleAllRowsSelectedProps }) => {
              return (
                <>
                  <IndeterminateCheckbox
                    {...getToggleAllRowsSelectedProps()}
                    classes={{
                      root: `${classes.selectAll} ${classes.checkbox}`
                    }}
                  />
                </>
              );
            },
            Cell: ({ row }) => {
              if (showCheckbox && !showCheckbox(row.original)) return <></>;
              return (
                <>
                  <IndeterminateCheckbox
                    classes={{ root: classes.checkbox }}
                    {...row.getToggleRowSelectedProps()}
                  />
                </>
              );
            }
          },
          ...columns
        ]);
      }
    }
  );

  const infiniteLoaderRef = useRef(null);

  useEffect(() => {
    if (updateSelection) updateSelection(selectedFlatRows);
  }, [selectedFlatRows]);

  useEffect(() => {
    if (
      state.pageIndex === 1 &&
      infiniteLoaderRef &&
      infiniteLoaderRef.current
    ) {
      infiniteLoaderRef.current._listRef.scrollTo(0, 0);
    }
    onStateChange(state);
  }, [state.pageIndex]);

  useEffect(() => {
    if (state.pageIndex !== 1) {
      gotoPage(1);
    } else {
      onStateChange(state);
    }
  }, [state.sortBy, state.filters]);

  const handleTableCellClick = (row, cell) => {
    if (cell.column.id !== "selection" && detailPanel) {
      toggleRowExpanded(row.id);
    }
    if (onRowClick) {
      onRowClick(row, cell);
    }
  };

  const handleLoadMoreItems = startIndex => {
    gotoPage(Math.floor(startIndex / pageSize) + 1);
  };

  function renderRow({ index, style }) {
    const row = rows[index];
    if (!row) return <div />;
    prepareRow(row);
    const { style: rowStyle, ...restRow } = row.getRowProps({ style });
    return (
      <>
        <TableRow
          className={clsx(classes.rowClass, {
            [classes.rowExpanded]: row.isExpanded
          })}
          hover={(!!detailPanel && !row.isExpanded) || !!onRowClick}
          {...restRow}
          style={{ ...rowStyle }}
          component="div"
        >
          {row.cells.map(cell => {
            return (
              <TableCell
                onClick={() => handleTableCellClick(row, cell)}
                {...cell.getCellProps(cellProps)}
                component="div"
                size={cellSize}
              >
                {cell.render("Cell")}
              </TableCell>
            );
          })}
        </TableRow>
        {row.isExpanded && (
          <TableRow className={classes.expansionPanel}>
            <TableCell colSpan={visibleColumns.length}>
              {detailPanel(row.original)}
            </TableCell>
          </TableRow>
        )}
      </>
    );
  }
  return (
    <TableContainer className={classes.tableContainer}>
      <MuiTable
        {...getTableProps()}
        className={classes.table}
        aria-label="sticky table"
        component="div"
      >
        <div style={{ position: "relative", flex: 1, zIndex: 0 }}>
          <InfiniteLoader
            isItemLoaded={index => data[index] !== undefined}
            loadMoreItems={handleLoadMoreItems}
            itemCount={totalCount}
            minimumBatchSize={pageSize}
            threshold={Math.floor(pageSize / 2)}
            ref={infiniteLoaderRef}
          >
            {({ onItemsRendered, ref }) => {
              return (
                <FixedSizeList
                  height={tableHeight}
                  itemCount={totalCount}
                  itemSize={rowHeight}
                  width="100%"
                  onItemsRendered={onItemsRendered}
                  ref={ref}
                  innerElementType={({ children, style, ...rest }) => (
                    <>
                      <div className={classes.header}>
                        <div>
                          <TableHead
                            component="div"
                            className={classes.tableHead}
                          >
                            {headerGroups.map((headerGroup, index) => (
                              <Fragment key={index}>
                                <TableRow
                                  {...headerGroup.getHeaderGroupProps()}
                                  component="div"
                                  className={classes.headerCaptionRow}
                                  key={index}
                                >
                                  {headerGroup.headers.map(column => (
                                    <TableCell
                                      className={classes.headerCell}
                                      {...column.getHeaderProps(headerProps)}
                                      component="div"
                                    >
                                      <Typography
                                        variant="subtitle2"
                                        sx={{
                                          display: "flex",
                                          alignItems: "center"
                                        }}
                                        noWrap
                                      >
                                        {(column.id !== "selection" ||
                                          showSelectAllCheckbox) &&
                                          column.render("Header")}
                                        <Box
                                          display="flex"
                                          alignItems="center"
                                          ml={1}
                                        >
                                          {column.canSort && (
                                            <SortCell
                                              setSortBy={setSortBy}
                                              column={column}
                                            />
                                          )}
                                        </Box>
                                      </Typography>
                                    </TableCell>
                                  ))}
                                </TableRow>
                                <TableRow
                                  {...headerGroup.getHeaderGroupProps()}
                                  component="div"
                                >
                                  {headerGroup.headers.map(column => (
                                    <TableCell
                                      className={classes.filter}
                                      {...column.getHeaderProps(headerProps)}
                                      component="div"
                                    >
                                      {column.Filter ? (
                                        column.render("Filter")
                                      ) : (
                                        <></>
                                      )}
                                    </TableCell>
                                  ))}
                                </TableRow>
                              </Fragment>
                            ))}
                          </TableHead>
                        </div>
                      </div>
                      <TableBody
                        {...getTableBodyProps()}
                        {...rest}
                        className={classes.tableBody}
                        style={style}
                        component="div"
                      >
                        {(data && data.length > 0) || loading ? (
                          children
                        ) : (
                          <TableRow
                            component="div"
                            className={classes.noRecordsRow}
                          >
                            <TableCell
                              colSpan={visibleColumns.length}
                              component="div"
                              className={classes.noRecordsCell}
                            >
                              <Typography
                                variant="body2"
                                sx={{ textAlign: "center" }}
                              >
                                {t("table.noRecordsMsg")}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                      {loading && (
                        <div className={classes.loadingOverlay}>
                          <CircularProgressOverlay />
                        </div>
                      )}
                    </>
                  )}
                >
                  {renderRow}
                </FixedSizeList>
              );
            }}
          </InfiniteLoader>
        </div>
      </MuiTable>
    </TableContainer>
  );
};

const MemoizedTable = props => {
  return useMemo(() => {
    return <Table {...props} />;
  }, [props.data, props.columns, props.loading]);
};

export default MemoizedTable;

MemoizedTable.propTypes = {
  cellSize: PropTypes.oneOf(["small", "medium"]),
  rowHeight: PropTypes.number
};

MemoizedTable.defaultProps = {
  manualSortBy: true,
  manualFilters: true,
  cellSize: "medium",
  rowHeight: 56,
  tableHeight: 522
};
